import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ClipboardCheck, Check, RotateCcw, Archive, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { SceneQcPanel, CATEGORY_LABELS } from './SceneQcPanel';
import { QcAccuracyLog } from './QcAccuracyLog';
import { PenthouseProgress } from './PenthouseProgress';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/fabrics/scene-review`;
const abs = (u) => (!u ? null : u.startsWith('http') ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);
const nice = (s) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
const STATUSES = ['pending', 'approved', 'kept', 'redo', 'all'];
const QC_OPTIONS = ['needs_review', 'fail', 'borderline', 'pass', 'unchecked'];
const QC_TONE = { pass: 'text-green-700', fail: 'text-red-700', borderline: 'text-amber-700' };

const Select = ({ value, onChange, options, label, testId }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
    className="h-8 rounded-md border border-neutral-200 bg-white px-2 text-xs text-neutral-700">
    <option value="">{label}</option>
    {options.map(o => <option key={o} value={o}>{nice(o)}</option>)}
  </select>
);

/* One pending scene: new vs live side by side + Approve / Redo / Keep existing */
const ReviewCard = ({ item, onAction, busy }) => (
  <div className="rounded-lg border border-neutral-200 overflow-hidden bg-white" data-testid={`review-card-${item.id}`}>
    <div className="grid grid-cols-2 gap-px bg-neutral-200">
      {[['New', item.url], ['Live now', item.live_url]].map(([label, url]) => (
        <div key={label} className="relative bg-neutral-100 aspect-square">
          {url ? <img src={abs(url)} alt={label} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
            : <span className="absolute inset-0 flex items-center justify-center text-[11px] text-neutral-400">No live scene</span>}
          <span className={`absolute top-1.5 left-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded ${label === 'New' ? 'bg-blue-600 text-white' : 'bg-white/90 text-neutral-700'}`}>{label}</span>
        </div>
      ))}
    </div>
    <div className="p-2 space-y-1.5">
      <p className="text-xs font-medium text-neutral-900 truncate">{item.color_name} <span className="text-neutral-400 font-normal">· {item.collection_name}</span></p>
      <p className="text-[11px] text-neutral-500">{nice(item.style || 'scene')} · {nice(item.room_type)} · <span data-testid={`review-status-${item.id}`} className="font-medium">{item.status}</span></p>
      {item.qc && (
        <p className={`text-[11px] ${QC_TONE[item.qc.verdict]}`} data-testid={`review-qc-${item.id}`}>
          <span className="font-semibold uppercase">QC {item.qc.verdict}</span>{item.qc.category ? <span className="ml-1 rounded bg-neutral-100 px-1 text-neutral-700" data-testid={`review-qc-category-${item.id}`}>{CATEGORY_LABELS[item.qc.category] || item.qc.category}</span> : null} — {item.qc.reason}
        </p>
      )}
      {item.status === 'pending' && (
        <div className="flex gap-1">
          <Button size="sm" className="h-7 flex-1 text-[11px] gap-1 bg-green-700 hover:bg-green-800" disabled={busy} onClick={() => onAction(item, 'approve')} data-testid={`review-approve-${item.id}`}><Check className="w-3 h-3" />Approve</Button>
          <Button size="sm" variant="outline" className="h-7 flex-1 text-[11px] gap-1" disabled={busy} onClick={() => onAction(item, 'redo')} data-testid={`review-redo-${item.id}`}><RotateCcw className="w-3 h-3" />Redo</Button>
          <Button size="sm" variant="outline" className="h-7 flex-1 text-[11px] gap-1" disabled={busy} onClick={() => onAction(item, 'keep')} data-testid={`review-keep-${item.id}`}><Archive className="w-3 h-3" />Keep existing</Button>
        </div>
      )}
      {item.status === 'pending' && item.qc && (
        item.qc.flagged_wrong
          ? <p className="text-[10px] text-neutral-400" data-testid={`review-qc-flagged-${item.id}`}>QC flagged as wrong</p>
          : <button className="text-[10px] text-neutral-500 hover:text-neutral-800 underline" disabled={busy} onClick={() => onAction(item, 'qc-wrong')} data-testid={`review-qc-wrong-${item.id}`}>QC wrong?</button>
      )}
    </div>
  </div>
);

export const SceneReviewDialog = ({ token }) => {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(0);
  const [data, setData] = useState({ items: [], total: 0, facets: {}, status_counts: {} });
  const [f, setF] = useState({ collection: '', fabric: '', style: '', room: '', status: 'pending', qc: '', category: '' });
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [logKey, setLogKey] = useState(0);
  const [spotDone, setSpotDone] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  useEffect(() => { if (open) axios.get(`${API}/qc/spot-check/summary`, { headers }).then(({ data: d }) => setSpotDone(d.complete)).catch(() => {}); }, [open, logKey, headers]);

  const loadCount = useCallback(() => axios.get(`${API}/count`, { headers }).then(({ data: d }) => setPending(d.pending)).catch(() => {}), [headers]);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
      const { data: d } = await axios.get(API, { headers, params });
      setData(d);
    } catch { toast.error('Failed to load scenes'); }
    setLoading(false);
  }, [f, headers]);

  useEffect(() => { loadCount(); const t = setInterval(loadCount, 30000); return () => clearInterval(t); }, [loadCount]);
  useEffect(() => { if (open) { const t = setTimeout(load, 250); return () => clearTimeout(t); } return undefined; }, [open, load]);

  const act = async (item, action) => {
    let body = {};
    if (action === 'qc-wrong') {
      const note = window.prompt(`Mark QC "${item.qc.verdict}" as wrong for ${item.color_name}? Optional note:`, '');
      if (note === null) return;
      body = { note };
    }
    setBusy(true);
    try {
      await axios.post(`${API}/${item.id}/${action}`, body, { headers });
      toast.success(action === 'approve' ? 'Approved — now live' : action === 'redo' ? 'Regenerating — the new version will appear as pending' : action === 'qc-wrong' ? 'Logged — QC disagreement recorded' : 'Kept the existing scene');
      setLogKey(k => k + 1);
      await Promise.all([load(), loadCount()]);
      setLogKey(k => k + 1);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Action failed'); }
    setBusy(false);
  };
  const bulk = async (action) => {
    const ids = data.items.filter(i => i.status === 'pending').map(i => i.id);
    if (!ids.length || !window.confirm(`${action === 'approve' ? 'Approve' : 'Keep existing for'} ${ids.length} visible pending scenes?`)) return;
    setBusy(true);
    try {
      const { data: d } = await axios.post(`${API}/bulk`, { ids, action }, { headers });
      toast.success(`${d.done} scenes ${action === 'approve' ? 'approved' : 'kept as existing'}`);
      setLogKey(k => k + 1);
      await Promise.all([load(), loadCount()]);
    } catch { toast.error('Bulk action failed'); }
    setBusy(false);
  };
  const set = (k) => (v) => setF(p => ({ ...p, [k]: v }));
  const visiblePending = data.items.filter(i => i.status === 'pending').length;

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) loadCount(); }}>
      <DialogTrigger asChild>
        <Button variant="outline" className="flex items-center gap-2 relative" data-testid="scene-review-btn">
          <ClipboardCheck className="w-4 h-4" /> Review Scenes
          {pending > 0 && <span className="ml-1 rounded-full bg-amber-600 text-white text-[10px] font-bold px-1.5 py-0.5" data-testid="scene-review-badge">{pending}</span>}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Batch Scene Review</DialogTitle></DialogHeader>
        <p className="text-xs text-neutral-500 -mt-2">New scenes stay pending until you review them. Approve makes a scene live; Redo regenerates it (still pending); Keep existing leaves the live scene untouched.</p>
        <SceneQcPanel api={API} headers={headers} onDone={load} onPickQc={(style, qc) => setF(p => ({ ...p, style, qc, category: '', status: 'pending' }))}
          onPickCategory={(style, category) => setF(p => ({ ...p, style, category, qc: '', status: 'pending' }))} onLogged={() => setLogKey(k => k + 1)} refreshKey={logKey} />
        <QcAccuracyLog api={API} headers={headers} refreshKey={logKey} />
        <PenthouseProgress api={API} headers={headers} refreshKey={logKey} onChanged={() => { load(); loadCount(); setLogKey(k => k + 1); }}
          onOpenQueue={() => setF(p => ({ ...p, collection: '', fabric: '', style: '', category: '', room: 'penthouse_living', status: 'pending', qc: 'needs_review' }))} />
        {f.qc === 'needs_review' && (
          <p className="text-xs text-amber-800 bg-amber-50 rounded px-2 py-1" data-testid="manual-review-queue-banner">
            Manual Review Queue — every pending Borderline, Fail and QC-wrong scene. Approve, Redo or Keep existing each one by hand.
          </p>
        )}
        <div className="flex flex-wrap items-center gap-2" data-testid="scene-review-filters">
          <Select value={f.collection} onChange={set('collection')} options={data.facets.collection_name || []} label="All collections" testId="review-filter-collection" />
          <Input value={f.fabric} onChange={(e) => set('fabric')(e.target.value)} placeholder="Fabric / colour" className="h-8 w-40 text-xs" data-testid="review-filter-fabric" />
          <Select value={f.style} onChange={set('style')} options={data.facets.style || []} label="All styles" testId="review-filter-style" />
          <Select value={f.room} onChange={set('room')} options={data.facets.room_type || []} label="All rooms" testId="review-filter-room" />
          <select value={f.status} onChange={(e) => set('status')(e.target.value)} data-testid="review-filter-status" className="h-8 rounded-md border border-neutral-200 bg-white px-2 text-xs text-neutral-700">
            {STATUSES.map(s => <option key={s} value={s}>{nice(s)}{s !== 'all' && data.status_counts[s] ? ` (${data.status_counts[s]})` : ''}</option>)}
          </select>
          <Select value={f.qc} onChange={set('qc')} options={QC_OPTIONS} label="All QC results" testId="review-filter-qc" />
          <select value={f.category} onChange={(e) => set('category')(e.target.value)} data-testid="review-filter-category" className="h-8 rounded-md border border-neutral-200 bg-white px-2 text-xs text-neutral-700">
            <option value="">All issue types</option>
            {Object.entries(CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <span className="text-xs text-neutral-500 ml-auto" data-testid="review-total">{data.total} scenes</span>
          <Button size="sm" className="h-8 text-xs bg-green-700 hover:bg-green-800" disabled={busy || !visiblePending || !spotDone} onClick={() => bulk('approve')} data-testid="review-bulk-approve"
            title={spotDone ? 'Approves visible QC Pass scenes of Ready styles only' : 'Locked until the spot-check process is complete'}>{spotDone ? 'Approve visible QC passes' : 'Approve all visible (locked)'}</Button>
          <Button size="sm" variant="outline" className="h-8 text-xs" disabled={busy || !visiblePending} onClick={() => bulk('keep')} data-testid="review-bulk-keep">Keep all visible</Button>
        </div>
        {loading ? <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-neutral-400" /></div>
          : data.items.length === 0 ? <p className="py-12 text-center text-sm text-neutral-500" data-testid="review-empty">No scenes match these filters.</p>
            : <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="review-grid">
              {data.items.map(item => <ReviewCard key={item.id} item={item} onAction={act} busy={busy} />)}
            </div>}
      </DialogContent>
    </Dialog>
  );
};
